import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  listAlerts,
  listTrayAlerts,
  countAlerts,
  createAlert,
  updateAlertStatus,
  updateAlertNote,
  clearAlerts,
} from "./api";
import type {
  AlertClearScope,
  AlertListParams,
  AlertStatus,
  CreateAlertPayload,
} from "./types";

const ALERTS_KEY = ["alerts"];
const ALERTS_COUNT_KEY = ["alerts", "count"];
const ALERTS_TRAY_KEY = ["alerts", "tray"];

export function useAlerts(params?: AlertListParams, enabled = true) {
  return useQuery({
    queryKey: [...ALERTS_KEY, params],
    queryFn: () => listAlerts(params),
    enabled,
    placeholderData: keepPreviousData,
    refetchInterval: params?.status === "sent" ? 30_000 : false,
  });
}

export function useAlertCounts() {
  return useQuery({
    queryKey: ALERTS_COUNT_KEY,
    queryFn: countAlerts,
    refetchInterval: 30_000,
  });
}

export function useTrayAlerts(params?: AlertListParams, enabled = true) {
  return useQuery({
    queryKey: [...ALERTS_TRAY_KEY, params],
    queryFn: () => listTrayAlerts(params),
    enabled,
    refetchInterval: 30_000,
  });
}

function invalidateAlertQueries(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ALERTS_KEY });
}

export function useUpdateAlertStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AlertStatus }) =>
      updateAlertStatus(id, status),
    onSuccess: () => {
      invalidateAlertQueries(qc);
    },
    onError: (error) => {
      if (error instanceof ApiError && [404, 409].includes(error.status)) {
        invalidateAlertQueries(qc);
      }
    },
  });
}

export function useClearAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope: AlertClearScope) => clearAlerts(scope),
    onSuccess: () => {
      invalidateAlertQueries(qc);
    },
  });
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAlertPayload) => createAlert(payload),
    onSuccess: () => {
      invalidateAlertQueries(qc);
    },
  });
}

export function useUpdateAlertNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      updateAlertNote(id, note),
    onSuccess: () => invalidateAlertQueries(qc),
  });
}
